import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useTranslation} from 'react-i18next';

import {Badge, Box, Divider as MUIDivider} from '@mui/material';
import {SideNav, ToggleButton} from '@app/components/common/SideNav';

import LoopIcon from '@mui/icons-material/Loop';
import PolygonIcon from '@apps-common/ui-icons/Polygon';
import SettingsIcon from '@mui/icons-material/Settings';

import LocaleModal from '@app/components/LocaleModal';
import {DRAWERS} from '@app/constants';
import {showLeftDrawer} from '@app/actions/ui';

import {
  countUploadsInProgress,
  listMigrated,
  useListUploads,
} from '@api/asyncIngest';
import {isDev} from '@app/config';
import {queryKeys} from '@api/collections';
import {useQueryClient} from '@tanstack/react-query';
import {withDarkTheme} from '@app/styles';

const Divider = props => (
  <MUIDivider variant="middle" sx={{marginX: 1}} {...props} />
);

const LeftNav = ({}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {asyncFeatureUploads: useAsyncUploads} = useFlags();
  const queryClient = useQueryClient();

  const activeDrawer = useSelector(state => state.ui.drawers.left);

  const onToggleDrawer = (event, drawer) => {
    // if the drawer is active, clear it.
    dispatch(showLeftDrawer(activeDrawer === drawer ? '' : drawer));
  };

  // notify users when uploads need attention
  const {data: uploads} = useListUploads({enabled: useAsyncUploads});
  const uploadsInProgress = useMemo(
    () => countUploadsInProgress(uploads),
    [uploads]
  );

  // simple state value to track if any of our uploads are currently migrating
  // to collections. We use this to trigger a refresh of the collections
  // query.
  const [previouslyMigrated, setPreviouslyMigrated] = useState(null);

  useEffect(() => {
    if (uploads === undefined) {
      return;
    }

    // the first time, just set the baseline migrated count
    if (previouslyMigrated === null) {
      setPreviouslyMigrated(listMigrated(uploads));
      return;
    }

    const migratedUploads = listMigrated(uploads);

    // check if the migrated uploads have changed
    if (
      migratedUploads?.length !== previouslyMigrated.length ||
      !migratedUploads.every((v, i) => {
        return previouslyMigrated[i] === v;
      })
    ) {
      setPreviouslyMigrated(listMigrated(uploads));
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});
    }
  }, [uploads, previouslyMigrated, queryClient]);

  return (
    <SideNav>
      <Box flexGrow={1} zIndex="inherit">
        <ToggleButton
          value={DRAWERS.COLLECTIONS}
          selected={activeDrawer === DRAWERS.COLLECTIONS}
          onClick={onToggleDrawer}
          title="Areas of interest"
          data-testid={DRAWERS.COLLECTIONS}
        >
          <PolygonIcon />
        </ToggleButton>
      </Box>
      <div>
        <Divider />
        {useAsyncUploads && (
          <ToggleButton
            value={DRAWERS.UPLOADS}
            selected={activeDrawer === DRAWERS.UPLOADS}
            onClick={onToggleDrawer}
            title="File Uploads"
          >
            <Badge badgeContent={uploadsInProgress} color="error">
              <LoopIcon />
            </Badge>
          </ToggleButton>
        )}
        {isDev && <LocaleModal />}
        <ToggleButton
          value={DRAWERS.SETTINGS}
          selected={activeDrawer === DRAWERS.SETTINGS}
          onClick={onToggleDrawer}
          title={t('navbars.tooltipSettings')}
        >
          <SettingsIcon />
        </ToggleButton>
      </div>
    </SideNav>
  );
};

export default withDarkTheme(LeftNav);
